import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { KeyboardShortcutLayout } from './layouts/keyboard-shortcut.layout';
import { KeyboardAvailabilityService } from 'common/keyboard-service/keyboard-availability.service';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { ClosingConfirmationService } from 'shared/components/confirmation-modal/confirmation-modal.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { CommonAuthService } from 'common/auth/common-auth.service';
import { KeyboardShortcutCombination } from './models/keyboard-shortcut.model';

@Component({
  selector: 'app-keyboard-shortcuts-menu',
  templateUrl: './keyboard-shortcuts-menu.component.html',
  styleUrls: ['./keyboard-shortcuts-menu.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class KeyboardShortcutsMenuComponent implements OnInit {
  @ViewChild('keyboardPanel')
  keyboardPanelContainer: ElementRef;

  isShortcutsMenuOpened = false;
  endingConfirmationList: string[];
  isBoldEnabled: boolean;

  readonly keyboardShortcutList = KeyboardShortcutLayout;
  readonly focusEventName = broadcastEvents.focusKeyboardShortcutsPanel;

  constructor(
    private _rxEmitter: RxEmitter,
    private _changeDetector: ChangeDetectorRef,
    private _closingConfirmationService: ClosingConfirmationService,
    private _keyboardAvailabilityService: KeyboardAvailabilityService,
    private _commonAuth: CommonAuthService
  ) {}

  async ngOnInit() {
    this.isBoldEnabled = await this._commonAuth.isBoldEnabled();
    this.endingConfirmationList = this.isBoldEnabled
      ? [WorkItemTypes.CHAT, WorkItemTypes.SMS, WorkItemTypes.EMAIL]
      : [WorkItemTypes.CHAT, WorkItemTypes.EMAIL];
    this._rxEmitter.getNamespaceObservable(MessageBusEvents.OpenSettingsMenu).subscribe(() => {
      this.toggleKeyboardShortcutsMenu();
    });
  }

  toggleKeyboardShortcutsMenu() {
    this.isShortcutsMenuOpened = !this.isShortcutsMenuOpened;
    this._changeDetector.detectChanges();

    if (this.isShortcutsMenuOpened) {
      this.setFocusOnKeyboardShortcutsMenu();
    }
  }

  setFocusOnKeyboardShortcutsMenu() {
    this.keyboardPanelContainer.nativeElement.focus();
  }

  handleKeyDown(event) {
    event.preventDefault();
    event.stopPropagation();

    this.closeKeyboardShortcutsMenu();
  }

  closeKeyboardShortcutsMenu() {
    if (this.isShortcutsMenuOpened) {
      this.isShortcutsMenuOpened = false;
    }
  }

  getKeyboardShortcut(keyboardCombination: string[]): string {
    return keyboardCombination.join('+');
  }

  toggleShortcutAvailability(keyboardCombination: string[]) {
    const shortcut = this.getKeyboardShortcut(keyboardCombination);
    this._keyboardAvailabilityService.toggleShortcutAvailability(shortcut);
  }

  isShortcutDisabled(keyboardCombination: string[]): boolean {
    const shortcut = this.getKeyboardShortcut(keyboardCombination);
    return this._keyboardAvailabilityService.isShortcutDisabled(shortcut);
  }

  hasVisibleShortcuts(shortcuts: KeyboardShortcutCombination[]): boolean {
    return shortcuts.some((shortcut) => this.isShortcutVisible(shortcut));
  }

  isShortcutVisible(shortcut: KeyboardShortcutCombination): boolean {
    return this.isBoldEnabled || !shortcut.isBoldFeature;
  }

  toggleEndingConfirmation(workItemType): void {
    const endingConfirmStatus = this.isEndingConfirmDisabled(workItemType);
    this._closingConfirmationService.setClosingConfirmation(workItemType, !endingConfirmStatus);
  }

  isEndingConfirmDisabled(workItemType): boolean {
    return !!this._closingConfirmationService.getClosingConfirmationSetting(workItemType);
  }

  getConfirmTitle(workItemType): string {
    switch (workItemType) {
      case WorkItemTypes.CHAT:
        return 'bcKeyboardShortcuts.settings.confirmEndChat';
      case WorkItemTypes.EMAIL:
        return 'bcKeyboardShortcuts.settings.confirmEndEmail';
      case WorkItemTypes.SMS:
        return 'bcKeyboardShortcuts.settings.confirmEndMessage';
    }
    throw new Error('Unhandled workItem type');
  }
}
